import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Container,
  Typography,
  Button,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Avatar,
  Switch,
  IconButton,
  Box,
  TextField,
  InputAdornment,
} from "@mui/material";
import { Add, Edit, Search } from "@mui/icons-material";
import { Navigate, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { blockUser, editUser, getUserByType } from "../redux/slices/user/thunk";
import LoadingComponent from "../components/LoadingComponent";

const AdminManagementPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const adminUsers = useSelector((state) => state.usersSlice.usersByType);
  const usersLoading = useSelector((state) => state.usersSlice.usersLoading);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const user = JSON.parse(localStorage.getItem('user'));

  useEffect(() => {
    dispatch(getUserByType({ type: "admin" }));
  }, [dispatch]);

const handleAddAdmin = () => navigate("/addUser", { state: { admin: true } });
  const handleEdit = (id) => navigate(`/editUser/${id}`);

  const filteredAdmins = useMemo(() => {
    if (!searchTerm.trim()) return adminUsers;
    const term = searchTerm.toLowerCase().trim();
    return adminUsers.filter((adm) => {
      const nameMatch = adm.userName?.toLowerCase().includes(term) || false;
      const emailMatch = adm.email?.toLowerCase().includes(term) || false;
      const phoneFields = [adm.phoneNumber || "", adm.countryCode?.replace("+", "") || ""].join(" ");
      const phoneMatch = phoneFields.toLowerCase().includes(term);
      return nameMatch || emailMatch || phoneMatch;
    });
  }, [adminUsers, searchTerm]);

  if (user?.type !== "admin" || !user?.superAdmin ) return <Navigate to="/" replace />;
  if (usersLoading) return <LoadingComponent />;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">{t("adminManagement.title", "Manage Admins")}</Typography>
        <Button variant="contained" startIcon={<Add sx={{ ml: 1 }} />} onClick={handleAddAdmin}>
          {t("adminManagement.addAdmin", "Add Admin")}
        </Button>
      </Box>

      <Box mb={3}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder={t("adminManagement.searchPlaceholder", "Search admins...")}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            sx: { borderRadius: 1, height: 50, backgroundColor: "background.paper" }
          }}
        />
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="center">{t("adminManagement.table.image", "Image")}</TableCell>
              <TableCell align="center">{t("adminManagement.table.name", "Name")}</TableCell>
              <TableCell align="center">{t("adminManagement.table.phone", "Phone")}</TableCell>
              <TableCell align="center">{t("adminManagement.table.email", "Email")}</TableCell>
              <TableCell align="center">{t("adminManagement.table.status", "Status")}</TableCell>
              <TableCell align="center">{t("adminManagement.table.edit", "Edit")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAdmins.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1">
                    {searchTerm ? t("adminManagement.noResults", "No matching admins.") : t("adminManagement.noAdmins", "No admins found.")}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredAdmins.map((adm) => (
                <TableRow key={adm._id}>
                  <TableCell align="center">
                    <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                      {adm.image ? <Avatar src={adm.image} alt={adm.userName} /> : <Avatar>{adm.userName?.charAt(0).toUpperCase()}</Avatar>}
                    </Box>
                  </TableCell>
                  <TableCell align="center">{adm.userName}</TableCell>
                  <TableCell align="center">{adm.countryCode?.replace("+", "")}{adm.phoneNumber}</TableCell>
                  <TableCell align="center">{adm.email}</TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={adm.status}
                      disabled={loading}
                      color="primary"
                      onClick={async () => {
                        try {
                          setLoading(true);
                          await dispatch(blockUser({ id: adm._id, data: { status: !adm.status } }));
                          await dispatch(getUserByType({ type: "admin" }));
                        } finally {
                          setLoading(false);
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton color="primary" onClick={() => handleEdit(adm._id)}>
                      <Edit />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default AdminManagementPage;
